import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCourseDetail, createCourse, updateCourse } from '../api/coursesApi';
import api from '../../../../api/client';
import { appToast } from '../../../../components/AppToast';

export function useCoursesEntryScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingCourseId = searchParams.get('id');

  const [form, setForm] = useState({
    name: '',
    short_name: '',
    department_id: '',
    company_id: '',
    show_current: true,
    show_previous: false,
  });

  const [operationalStatus, setOperationalStatus] = useState('active');
  const [isLoading, setIsLoading] = useState(Boolean(editingCourseId));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [companyOptions, setCompanyOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  // Load master dropdown options
  useEffect(() => {
    async function loadOptions() {
      try {
        const [compRes, deptRes] = await Promise.all([
          api.get('/options/companies'),
          api.get('/options/departments'),
        ]);
        const comps = (compRes.data || []).map((c) => ({
          value: String(c.id),
          label: c.name,
        }));
        const depts = (deptRes.data || []).map((d) => ({
          value: String(d.id),
          label: d.name,
        }));
        setCompanyOptions(comps);
        setDepartmentOptions(depts);

        // Preselect first company/department if creating new
        if (!editingCourseId) {
          setForm((prev) => ({
            ...prev,
            company_id: prev.company_id || (comps.length > 0 ? comps[0].value : ''),
            department_id: prev.department_id || (depts.length > 0 ? depts[0].value : ''),
          }));
        }
      } catch (err) {
        console.error('Failed to load master options:', err);
      }
    }
    loadOptions();
  }, [editingCourseId]);

  // Load existing course if editing
  useEffect(() => {
    async function loadIfEditing() {
      if (!editingCourseId) return;
      setIsLoading(true);
      setError('');
      try {
        const detail = await getCourseDetail(editingCourseId);
        if (detail) {
          setForm({
            name: detail.name || '',
            short_name: detail.short_name || '',
            department_id: detail.department_id ? String(detail.department_id) : '',
            company_id: detail.company_id ? String(detail.company_id) : '',
            show_current: detail.show_current ?? true,
            show_previous: detail.show_previous ?? false,
          });
          setOperationalStatus(detail.status === 'Active' ? 'active' : 'inactive');
        } else {
          setError('Course not found.');
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Failed to load course details.';
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      } finally {
        setIsLoading(false);
      }
    }
    loadIfEditing();
  }, [editingCourseId]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Course Name is required.');
      appToast.error('Course Name is required.');
      return;
    }
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: form.name.trim(),
        short_name: form.short_name ? form.short_name.trim() : null,
        department_id: form.department_id ? Number(form.department_id) : null,
        company_id: form.company_id ? Number(form.company_id) : null,
        show_current: Boolean(form.show_current),
        show_previous: Boolean(form.show_previous),
        status: operationalStatus === 'active' ? 'Active' : 'Archive',
      };

      if (editingCourseId) {
        await updateCourse(editingCourseId, payload);
        appToast.success('Course updated successfully.');
        setSuccess('Course updated successfully.');
      } else {
        await createCourse(payload);
        appToast.success('Course created successfully.');
        setSuccess('Course created successfully.');
      }

      setTimeout(() => {
        navigate('/masters/courses/list');
      }, 600);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to save course.';
      const errStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
      setError(errStr);
      appToast.error(errStr);
    } finally {
      setIsSaving(false);
    }
  };

  const completionPercent = useMemo(() => {
    const fields = ['name', 'short_name', 'department_id', 'company_id'];
    const filled = fields.filter((f) => String(form[f] || '').trim().length > 0).length;
    return Math.round((filled / fields.length) * 100);
  }, [form]);

  return {
    state: {
      form,
      operationalStatus,
      isLoading,
      isSaving,
      error,
      success,
      isEditMode: Boolean(editingCourseId),
      companyOptions,
      departmentOptions,
      completionPercent,
    },
    actions: {
      patchForm,
      setOperationalStatus,
      handleSave,
    },
  };
}
